// SSE subscription hook with auto-reconnect and Last-Event-ID resume.
//
// The gateway emits typed events, so we addEventListener per requested kind
// and keep onmessage as the untyped catch-all; the callback gets the kind too.
//
// We do our own close()+reconnect for exponential backoff (native EventSource
// only does fixed 3s retry). That discards the browser's Last-Event-ID, so we
// track the last id locally and resume via a `last_event_id=` query param the
// orchestrator honors alongside the standard header.

import { useEffect, useRef, useState } from 'react';
import type { SSEEvent, SSEKind } from './api';
import { api } from './api';

// Consecutive failed connects, with no successful open in between, before we
// ask whether the session is still alive.
//
// EventSource never exposes the HTTP status of a failed connect, so a dead
// session and a restarted orchestrator arrive here as the same `onerror`. The
// difference matters: one recovers by retrying and the other never does, and
// retrying the second forever is a request every backoff period for as long as
// the tab stays open.
//
// Not on the first failure, because the ordinary cause is a deploy and the
// stream comes back on its own. Three consecutive failures is past the point
// where a restart would have finished.
const AUTH_PROBE_AFTER_FAILURES = 3;

/**
 * The task transitions a view of submissions has to refresh on.
 *
 * Stated once rather than per page, because it is the wire's terminal
 * vocabulary and every page listing submissions needs all of it. Three pages
 * each carried their own literal and all three omitted `task.cancelled`, so a
 * cancelled run's row went on saying "running" while the bell said otherwise -
 * the hook subscribes with addEventListener per kind and the orchestrator sends
 * typed events, so a kind that is not in this list is not merely unhandled, it
 * is never delivered.
 *
 * `task.cancelled` is its own kind rather than a completion with a cancelled
 * status, because a cancelled run produced no report to fetch.
 */
export const TASK_STREAM_KINDS: readonly SSEKind[] = [
  'task.dispatched',
  'task.complete',
  'task.failed',
  'task.cancelled',
];

export function useSSE(
  url: string | null,
  onEvent: (e: SSEEvent, kind?: SSEKind) => void,
  eventKinds: ReadonlyArray<SSEKind> = [],
) {
  const cb = useRef(onEvent);
  cb.current = onEvent;
  const [connected, setConnected] = useState(false);

  // Stable key so the effect doesn't re-run on every render when the
  // caller passes a new array literal.
  const kindsKey = eventKinds.join(',');

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1000;
    let lastEventID: string | null = null;
    let failures = 0;
    let probing = false;

    // Ask whether the session is still alive, once, after enough consecutive
    // failures that a restart would have finished.
    //
    // The answer is delivered by `api`, not read here: any 401 it sees
    // broadcasts `voltz:session-expired`, AuthContext clears the user, and this
    // hook's caller passes `url = null`, which tears the effect down. So the
    // probe needs no result handling - it only has to travel a path that can
    // SEE a status, which EventSource cannot.
    //
    // A probe that fails for any other reason is the orchestrator still being
    // down, and leaves the retry loop exactly as it was.
    const probeSession = () => {
      if (probing) return;
      probing = true;
      void api
        .me()
        .catch(() => undefined)
        .finally(() => {
          probing = false;
        });
    };

    const buildURL = () => {
      if (lastEventID == null) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}last_event_id=${encodeURIComponent(lastEventID)}`;
    };

    const connect = () => {
      if (cancelled) return;
      es = new EventSource(buildURL(), { withCredentials: true });
      es.onopen = () => {
        backoff = 1000;
        failures = 0;
        setConnected(true);
      };
      es.onerror = () => {
        setConnected(false);
        es?.close();
        if (cancelled) return;
        failures += 1;
        if (failures === AUTH_PROBE_AFTER_FAILURES) probeSession();
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30_000);
      };
      const dispatch = (kind: SSEKind | undefined) => (msg: MessageEvent) => {
        // Remember the last seen id so a manual reconnect resumes from here.
        if (msg.lastEventId) lastEventID = msg.lastEventId;
        try {
          cb.current(JSON.parse(msg.data), kind);
        } catch {
          /* ignore malformed */
        }
      };
      es.onmessage = dispatch(undefined);
      for (const k of eventKinds) {
        es.addEventListener(k, dispatch(k) as EventListener);
      }
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer != null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      es?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, kindsKey]);

  return connected;
}
